<script setup lang="ts">
import { computed } from 'vue';
import { IconName, IconSize } from './icon.constants';

type Props = {
  /** Which glyph to render. */
  name: IconName;
  /** Rendered size. */
  size?: IconSize;
};

const props = withDefaults(defineProps<Props>(), {
  size: IconSize.MD,
});

// Pixel dimensions per size (replicating the Figma Icon Size variant 28/24/20/16/12).
const sizePx: Record<IconSize, number> = {
  [IconSize.XS]: 12,
  [IconSize.SM]: 16,
  [IconSize.MD]: 20,
  [IconSize.LG]: 24,
  [IconSize.XL]: 28,
};

const px = computed(() => sizePx[props.size]);

// Path data on a 24×24 grid. Icons inherit color via `currentColor` and
// scale down cleanly at smaller sizes. Copied from the admin icon assets.
// Entries are stroke-based by default; `{ d, filled: true }` renders the
// path as a solid fill instead (used for letterform glyphs like "JPG").
type IconPath = string | { d: string; filled: true };

const iconPaths: Record<IconName, IconPath[]> = {
  [IconName.ADD_CIRCLE]: [
    'M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z',
    'M8 12H16',
    'M12 16V8',
  ],
  [IconName.CLOSE]: ['M7.5 16L16 7.5', 'M16 16L7.5 7.5'],
  [IconName.DELETE]: [
    'M21 5.98C17.67 5.65 14.32 5.48 10.98 5.48C9 5.48 7.02 5.58 5.04 5.78L3 5.98',
    'M8.499 4.97L8.719 3.66C8.879 2.71 8.999 2 10.689 2H13.309C14.999 2 15.129 2.75 15.279 3.67L15.499 4.97',
    'M18.85 9.14L18.2 19.21C18.09 20.78 18 22 15.21 22H8.79C6 22 5.91 20.78 5.8 19.21L5.15 9.14',
    'M10.33 16.5H13.66',
    'M9.501 12.5H14.501',
  ],
  [IconName.DOTS_VERTICAL]: [
    {
      d: 'M14 5C14 3.9 13.1 3 12 3C10.9 3 10 3.9 10 5C10 6.1 10.9 7 12 7C13.1 7 14 6.1 14 5Z',
      filled: true,
    },
    {
      d: 'M14 19C14 17.9 13.1 17 12 17C10.9 17 10 17.9 10 19C10 20.1 10.9 21 12 21C13.1 21 14 20.1 14 19Z',
      filled: true,
    },
    {
      d: 'M14 12C14 10.9 13.1 10 12 10C10.9 10 10 10.9 10 12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12Z',
      filled: true,
    },
  ],
  [IconName.CHEVRON_LEFT]: ['M15 6L9 12L15 18'],
  [IconName.CHEVRON_RIGHT]: ['M9 6L15 12L9 18'],
  [IconName.PLUS]: ['M12 5V19', 'M5 12H19'],
  [IconName.MINUS]: ['M5 12H19'],
  [IconName.EYE]: [
    'M15.58 11.9999C15.58 13.9799 13.98 15.5799 12 15.5799C10.02 15.5799 8.42 13.9799 8.42 11.9999C8.42 10.0199 10.02 8.41992 12 8.41992C13.98 8.41992 15.58 10.0199 15.58 11.9999Z',
    'M12 20.27C15.53 20.27 18.82 18.19 21.11 14.59C22.01 13.18 22.01 10.81 21.11 9.39997C18.82 5.79997 15.53 3.71997 12 3.71997C8.47 3.71997 5.18 5.79997 2.89 9.39997C1.99 10.81 1.99 13.18 2.89 14.59C5.18 18.19 8.47 20.27 12 20.27Z',
  ],
  [IconName.DOCUMENT_DOWNLOAD]: [
    'M9 11V17L11 15',
    'M9 17L7 15',
    'M22 10V15C22 20 20 22 15 22H9C4 22 2 20 2 15V9C2 4 4 2 9 2H14',
    'M22 10H18C15 10 14 9 14 6V2L22 10Z',
  ],
  [IconName.FILE_PDF]: [
    'M4.75 11.25V5.75C4.75 4.64543 5.64543 3.75 6.75 3.75H14.1716C14.702 3.75 15.2107 3.96071 15.5858 4.33579L18.6642 7.41421C19.0393 7.78929 19.25 8.29799 19.25 8.82843V11.25M13.75 4.25V7.25C13.75 8.35457 14.6454 9.25 15.75 9.25H18.75',
    'M3.75 20.25V14.75H5.75C6.57843 14.75 7.25 15.4216 7.25 16.25C7.25 17.0784 6.57843 17.75 5.75 17.75H4.5',
    'M9.75 14.75V20.25H11.25C12.3333 20.25 14 19.7 14 17.5C14 15.3 12.3333 14.75 11.25 14.75H9.75Z',
    'M20.25 14.75H16.75V20.25',
    'M16.75 17.75H19.25',
  ],
  [IconName.FILE_IMAGE]: [
    'M4.75 11.25V5.75C4.75 4.64543 5.64543 3.75 6.75 3.75H14.1716C14.702 3.75 15.2107 3.96071 15.5858 4.33579L18.6642 7.41421C19.0393 7.78929 19.25 8.29799 19.25 8.82843V11.25M13.75 4.25V7.25C13.75 8.35457 14.6454 9.25 15.75 9.25H18.75',
    {
      d: 'M6.5 14C6.91421 14 7.25 14.3358 7.25 14.75V18.75C7.25 19.9926 6.24264 21 5 21H4.5C4.08579 21 3.75 20.6642 3.75 20.25C3.75 19.8358 4.08579 19.5 4.5 19.5H5C5.41421 19.5 5.75 19.1642 5.75 18.75V14.75C5.75 14.3358 6.08579 14 6.5 14Z',
      filled: true,
    },
    {
      d: 'M8.75 14.75C8.75 14.3358 9.08579 14 9.5 14H11.25C12.4926 14 13.5 15.0074 13.5 16.25C13.5 17.4926 12.4926 18.5 11.25 18.5H10.25V20.25C10.25 20.6642 9.91421 21 9.5 21C9.08579 21 8.75 20.6642 8.75 20.25V14.75ZM10.25 17H11.25C11.6642 17 12 16.6642 12 16.25C12 15.8358 11.6642 15.5 11.25 15.5H10.25V17Z',
      filled: true,
    },
    {
      d: 'M17.7537 15.524C17.5581 15.4756 17.3575 15.4641 17.2209 15.4724C16.5734 15.512 15.75 16.2463 15.75 17.5494C15.75 18.8354 16.4961 19.5275 17.3256 19.53C17.784 19.5236 18.2137 19.4123 18.5 19.2287V18.5761H18.1561C17.7419 18.5761 17.4061 18.2403 17.4061 17.8261C17.4061 17.4119 17.7419 17.0761 18.1561 17.0761H19.25C19.6642 17.0761 20 17.4119 20 17.8261V19.573C20 19.7606 19.9297 19.9414 19.803 20.0797C19.1384 20.8051 18.0966 21.0207 17.3391 21.0299L17.3299 21.03V21.03C15.4566 21.03 14.25 19.4349 14.25 17.5494C14.25 15.6788 15.4836 14.0758 17.1294 13.9752C17.4074 13.9583 17.7611 13.9806 18.114 14.0679C18.4605 14.1537 18.8732 14.3188 19.1993 14.6375C19.4955 14.9271 19.501 15.4019 19.2115 15.6981C18.9219 15.9944 18.4471 15.9998 18.1509 15.7103C18.0857 15.6466 17.9557 15.574 17.7537 15.524Z',
      filled: true,
    },
  ],
  [IconName.FILE_VIDEO]: [
    'M2.75 8.25C2.75 6.59315 4.09315 5.25 5.75 5.25H14.25C15.9069 5.25 17.25 6.59315 17.25 8.25V15.75C17.25 17.4069 15.9069 18.75 14.25 18.75H5.75C4.09315 18.75 2.75 17.4069 2.75 15.75V8.25Z',
    'M17.25 9L20.3439 7.62495C21.0051 7.33105 21.75 7.8151 21.75 8.53876V15.4612C21.75 16.1849 21.0051 16.669 20.3439 16.375L17.25 15',
  ],
  [IconName.FILE_AUDIO]: [
    'M9 18.5V6.5C9 5.4 9.55 4.84 11.02 4.5L16.02 3.28C17.72 2.88 19 3.78 19 5.5V16.5',
    'M6.5 21C7.88071 21 9 19.8807 9 18.5C9 17.1193 7.88071 16 6.5 16C5.11929 16 4 17.1193 4 18.5C4 19.8807 5.11929 21 6.5 21Z',
    'M16.5 19C17.8807 19 19 17.8807 19 16.5C19 15.1193 17.8807 14 16.5 14C15.1193 14 14 15.1193 14 16.5C14 17.8807 15.1193 19 16.5 19Z',
    'M9 9.5L19 7',
  ],
  [IconName.FILE_PRESENTATION]: [
    'M21.25 16.25V6.75C21.25 5.64543 20.3546 4.75 19.25 4.75H4.75C3.64543 4.75 2.75 5.64543 2.75 6.75V16.25C2.75 17.3546 3.64543 18.25 4.75 18.25H19.25C20.3546 18.25 21.25 17.3546 21.25 16.25Z',
    'M16 18.5L17 21.5M8 18.5L7 21.5M12 18.5V20.5',
    'M12 4.5V3',
  ],
  [IconName.FILE_DOCUMENT]: [
    'M4.75 19.25V5.75C4.75 4.64543 5.64543 3.75 6.75 3.75H14.1716C14.702 3.75 15.2107 3.96071 15.5858 4.33579L18.6642 7.41421C19.0393 7.78929 19.25 8.29799 19.25 8.82843V19.25C19.25 20.3546 18.3546 21.25 17.25 21.25H6.75C5.64543 21.25 4.75 20.3546 4.75 19.25Z',
    'M13.75 4.25V7.25C13.75 8.35457 14.6454 9.25 15.75 9.25H18.75',
  ],
};

const paths = computed(() =>
  iconPaths[props.name].map((path) =>
    typeof path === 'string' ? { d: path, filled: false } : path,
  ),
);
</script>

<template>
  <svg
    :width="px"
    :height="px"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    class="inline-block shrink-0"
  >
    <path
      v-for="(path, i) in paths"
      :key="i"
      :d="path.d"
      :fill="path.filled ? 'currentColor' : undefined"
      :stroke="path.filled ? 'none' : undefined"
      :fill-rule="path.filled ? 'evenodd' : undefined"
      :clip-rule="path.filled ? 'evenodd' : undefined"
    />
  </svg>
</template>
